import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Video, MessageSquare, Calendar, ArrowRight, Play, GraduationCap } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LivestockLessonsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog?category=livestock_lesson`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case "video":
        return <Video className="w-5 h-5" />;
      case "snippet":
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <BookOpen className="w-5 h-5" />;
    }
  };

  const getContentTypeLabel = (type) => {
    switch (type) {
      case "video":
        return "Video Lesson";
      case "snippet":
        return "Quick Tip";
      default:
        return "Blog Post";
    }
  };

  const getContentTypeColor = (type) => {
    switch (type) {
      case "video":
        return "bg-[#E63946] text-white";
      case "snippet":
        return "bg-[#E9C46A] text-[#1D3557]";
      default:
        return "bg-[#2A9D8F] text-white";
    }
  };

  const filteredPosts = filter === "all" 
    ? posts 
    : posts.filter(post => post.content_type === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-16 h-16 bg-[#2A9D8F] rounded-2xl flex items-center justify-center border-4 border-[#1D3557]">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Livestock Lessons with Callie
          </h1>
          <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
            Learn fun facts about farm animals, their care, and why they're so important! Callie guides you through the wonderful world of livestock.
          </p>
        </motion.div>

        {/* Content Type Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setFilter("all")}
            className={`px-6 py-3 rounded-2xl font-bold border-4 border-[#1D3557] transition-all flex items-center gap-2 ${
              filter === "all"
                ? "bg-[#1D3557] text-white shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
                : "bg-white text-[#1D3557] hover:shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
            }`}
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            data-testid="filter-all-btn"
          >
            All Content
          </button>
          <button
            onClick={() => setFilter("blog")}
            className={`px-6 py-3 rounded-2xl font-bold border-4 border-[#1D3557] transition-all flex items-center gap-2 ${
              filter === "blog"
                ? "bg-[#2A9D8F] text-white shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
                : "bg-white text-[#1D3557] hover:shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
            }`}
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            data-testid="filter-blog-btn"
          >
            <BookOpen className="w-5 h-5" />
            Blog Posts
          </button>
          <button
            onClick={() => setFilter("video")}
            className={`px-6 py-3 rounded-2xl font-bold border-4 border-[#1D3557] transition-all flex items-center gap-2 ${
              filter === "video"
                ? "bg-[#E63946] text-white shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
                : "bg-white text-[#1D3557] hover:shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
            }`}
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            data-testid="filter-video-btn"
          >
            <Video className="w-5 h-5" />
            Videos
          </button>
          <button
            onClick={() => setFilter("snippet")}
            className={`px-6 py-3 rounded-2xl font-bold border-4 border-[#1D3557] transition-all flex items-center gap-2 ${
              filter === "snippet"
                ? "bg-[#E9C46A] text-[#1D3557] shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
                : "bg-white text-[#1D3557] hover:shadow-[4px_4px_0px_0px_rgba(29,53,87,1)]"
            }`}
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            data-testid="filter-snippet-btn"
          >
            <MessageSquare className="w-5 h-5" />
            Quick Tips
          </button>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="tactile-card inline-block p-12">
              <GraduationCap className="w-16 h-16 text-[#2A9D8F] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Lessons Coming Soon!
              </h3>
              <p className="text-[#1D3557] font-medium max-w-md">
                Callie is preparing some amazing livestock lessons for you. Check back soon to learn about cows, pigs, sheep, and more farm friends!
              </p>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card overflow-hidden"
                data-testid={`lesson-card-${index}`}
              >
                {post.featured_image && (
                  <div className="relative">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    {post.content_type === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-[#E63946] ml-1" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-6">
                  <div className="mb-3">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-bold text-sm border-2 border-[#1D3557] ${getContentTypeColor(post.content_type)}`}>
                      {getContentTypeIcon(post.content_type)}
                      {getContentTypeLabel(post.content_type)}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {post.title}
                  </h2>
                  <p className="text-[#1D3557] font-medium mb-4 line-clamp-3">
                    {post.content.substring(0, 120)}...
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#1D3557]/70 font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.created_at)}
                    </span>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-[#E63946] font-bold flex items-center gap-1 hover:gap-2 transition-all"
                      data-testid={`read-more-${post.id}`}
                    >
                      {post.content_type === "video" ? "Watch" : "Read More"} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* What You'll Learn Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1D3557] mb-8" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            What You'll Learn
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Cow Facts", description: "Learn about different cow breeds, what they eat, and how they help farmers!", color: "bg-[#E9C46A]" },
              { title: "Pig Smarts", description: "Discover why pigs are some of the smartest animals on the farm!", color: "bg-[#E63946]" },
              { title: "Sheep & Wool", description: "Find out how sheep give us wool and why they stick together!", color: "bg-[#2A9D8F]" },
              { title: "Farm Life", description: "Explore what a day on the farm looks like for all the animals!", color: "bg-[#457B9D]" },
            ].map((topic, index) => (
              <motion.div
                key={topic.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card p-6 text-center"
              >
                <div className={`${topic.color} w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 border-3 border-[#1D3557]`}>
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {topic.title}
                </h3>
                <p className="text-sm text-[#1D3557] font-medium">
                  {topic.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 tactile-card p-8 md:p-12 bg-[#2A9D8F]/10 text-center"
        >
          <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Want to Learn More with Callie?
          </h3>
          <p className="text-[#1D3557] font-medium mb-6 max-w-2xl mx-auto">
            Follow along with Callie's adventures in The Detective Diaries of Marchfield Meadows and discover even more about farm life!
          </p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="learn-more-btn"
          >
            <BookOpen className="w-5 h-5" />
            Explore the Book
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default LivestockLessonsPage;
